import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import Webcam from 'react-webcam'
import { useDispatch, useSelector } from 'react-redux'
import { Stack } from '@mui/material'
import { CameraAlt as CameraAltIcon } from '@mui/icons-material'
import { useTheme } from '@mui/styles'
import { useCaptureImageMutation, useGetOrgImageQuery, useUploadOrgImageMutation } from 'services/assets'
import { dispatchSnackbar, b64toBlob } from 'utils/helpers'
import Loading from 'components/generic/Loading'
import DialogModal from '.'

const CapturePhotoModal = ({ onSave, onClose, open, photoImage, ...restProps }) => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const imageStyle = {
    color: theme.palette.text.hint,
    height: 342,
    width: 342,
    objectFit: 'contain',
    border: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.text.disabled,
  }

  const [captureImage, { isLoading: isCapturingImage }] = useCaptureImageMutation()

  const {
    data: orgImage,
    isFetching: isFetchingOrgImage,
    error,
  } = useGetOrgImageQuery(
    {
      filename: photoImage?.filename,
      isTemp: false,
    },
    { skip: !photoImage?.filename },
  )

  useEffect(() => {
    if (error?.data?.code === 'NOT_FOUND') {
      dispatchSnackbar(
        dispatch,
        'error',
        'Image was not captured successfully by the camera. Please delete and try capturing again.',
      )
    }
  }, [error])

  const [uploadOrgImage, { isLoading: isUploadingOrgImage }] = useUploadOrgImageMutation()

  const isLoading = isCapturingImage || isFetchingOrgImage || isUploadingOrgImage

  const { selectedCameraId } = useSelector(state => state.devices)
  const isNativeCamera = selectedCameraId === 'native'

  const [src, setSrc] = useState('')
  const [isViewMode, setIsViewMode] = useState(false)
  const [cameraActive, setCameraActive] = useState(true)
  const [isCameraReady, setIsCameraReady] = useState(false)
  const imageRef = useRef(null)

  const onSubmit = async () => {
    if (!isCameraReady && (isNativeCamera || !selectedCameraId)) {
      dispatchSnackbar(dispatch, 'error', 'Camera is not ready yet. Please wait.')
      return
    }

    if (isNativeCamera) {
      if (cameraActive) {
        setSrc(imageRef.current.getScreenshot() || '')
        setCameraActive(false)
      } else {
        const photoFormData = new FormData()
        photoFormData.append('image', b64toBlob(src))
        await uploadOrgImage({
          body: photoFormData,
        })
          .unwrap()
          .then(response => {
            onSave(response.imageName)
            dispatchSnackbar(dispatch, 'success', 'Photo uploaded successfully.')
          })
          .catch(error => {
            const errorMessage = error?.data?.error || 'There was an error uploading the photo'
            dispatchSnackbar(dispatch, 'error', errorMessage)
          })
        onClose()
      }
    } else {
      await captureImage({ deviceId: selectedCameraId })
        .unwrap()
        .then(response => {
          onSave(response.filename)
          dispatchSnackbar(dispatch, 'success', 'Photo uploaded successfully.')
        })
        .catch(error => {
          const errorMessage = error?.data?.error || 'There was an error uploading the photo'
          dispatchSnackbar(dispatch, 'error', errorMessage)
        })
    }
  }

  const onRetake = () => {
    setSrc('')
    if (isNativeCamera) {
      setIsViewMode(false)
      setCameraActive(true)
    } else {
      onSubmit()
    }
  }

  useEffect(() => {
    if (orgImage) {
      setSrc(orgImage)
      setIsViewMode(true)
      setCameraActive(false)
    }
  }, [orgImage])

  const handleCameraReady = () => {
    setIsCameraReady(true)
  }

  const renderImageComponent = () => {
    if (isLoading) {
      return <Loading localized />
    }
    if (src) {
      return <img src={src} alt="applicantPhoto" style={imageStyle} />
    }
    if (isNativeCamera) {
      return (
        <Webcam
          ref={imageRef}
          screenshotFormat="image/jpeg"
          screenshotQuality={1}
          forceScreenshotSourceSize
          height={342}
          width={342}
          videoConstraints={{
            height: 500,
            width: 500,
            facingMode: 'environment',
          }}
          onCanPlay={handleCameraReady}
        />
      )
    }
    return (
      <CameraAltIcon
        style={{
          ...imageStyle,
          height: 200,
          width: 200,
        }}
      />
    )
  }

  return (
    <DialogModal
      {...restProps}
      open={open}
      onClose={() => {
        if (isCapturingImage || isUploadingOrgImage) return
        onClose()
      }}
      onSave={isViewMode ? onClose : onSubmit}
      extraAction={
        src
          ? {
              text: 'Retake',
              onClick: onRetake,
            }
          : {}
      }
      saveButtonProps={{
        disabled: isLoading || (isNativeCamera && !isCameraReady),
      }}
      closeVisible={isNativeCamera}
      buttonTitle={src ? 'Done' : 'Take Photo'}
    >
      <Stack alignItems="center">{renderImageComponent()}</Stack>
    </DialogModal>
  )
}

CapturePhotoModal.propTypes = {
  ...DialogModal.propTypes,
  onSave: PropTypes.func,
  open: PropTypes.bool,
  photoImage: PropTypes.object,
}

CapturePhotoModal.defaultProps = {
  ...DialogModal.defaultProps,
  onSave: () => {},
  open: true,
  photoImage: {},
}

export default CapturePhotoModal
